import React, { useState } from 'react';
import {
    Box,
    Typography,
    Avatar,
    IconButton,
    InputBase,
    Divider,
    Paper,
    List,
    ListItemButton,
    ListItemAvatar,
    ListItemText,
    Icon,
    Button
} from '@mui/material';
import { useSignalR } from './chat.signalr.context';
import { keyUser, keyGroup, hasUnreadMessages } from '../../_helpers/chat.helper';

export function ChatSidebar({
    currentUser, users, groups, activeRecipient, activeGroup,
    onSelectUser, onSelectGroup, onGroupManage
}: {
    currentUser: any;
    users: any;
    groups: any[];
    activeRecipient: any;
    activeGroup: any;
    onSelectUser: (user: any) => void;
    onSelectGroup: (group: any) => void;
    onGroupManage?: (group?: any) => void;
}) {
    const [search, setSearch] = useState('');
    const { incomingMessages, setIncomingMessages } = useSignalR();
    const [clearedUnread, setClearedUnread] = useState<Record<string, boolean>>({});

    const filteredUsers = users
        .filter((u: { id: any; name: string }) =>
            u.id !== currentUser.id &&
            u.name.toLowerCase().includes(search.toLowerCase())
        );

    //Unread user IDs (excluding currently open chat)
    const unreadUserIds = incomingMessages
        ? incomingMessages
            .filter(
                (m: any) =>
                    m.toUserId === currentUser.id &&
                    (!activeRecipient || activeRecipient.id !== m.fromUserId)
            )
            .map((m: any) => m.fromUserId) : [];

    //Sort by -> unread first, then online, then name
    const sortedUsers = [...filteredUsers].sort((a: any, b: any) => {
        const aUnread = unreadUserIds.includes(a.id);
        const bUnread = unreadUserIds.includes(b.id);
        const aOnline = a.online;
        const bOnline = b.online;

        //Unread first
        if (aUnread !== bUnread) return aUnread ? -1 : 1;
        //Online first
        if (aOnline !== bOnline) return aOnline ? -1 : 1;
        //Then alphabetical by name
        return a.name.localeCompare(b.name);
    });

    return (
        <Paper
            elevation={0}
            sx={{
                width: '100%',
                maxWidth: '100%',
                minWidth: 0,
                height: '100%',
                boxSizing: 'border-box',
                borderRadius: 2,
                overflow: 'hidden',
                display: 'flex',
                flexDirection: 'column'
            }}
        >

            {/* Search Bar */}
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    backgroundColor: '#f1f1f1',
                    px: 1,
                    py: 0.5,
                    borderRadius: 2,
                    border: '1px solid #ccc',
                    margin: "10px 10px 0 10px"
                }}
            >
                <Icon fontSize="small" color="action">search</Icon>
                <InputBase
                    style={{ "borderRadius": "none" }}
                    placeholder="Search Contacts"
                    fullWidth
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    sx={{
                        ml: 1,
                        fontSize: 14,
                        lineHeight: 1.5,
                        height: '100%',
                    }}
                />
            </Box>

            {/*User and Groups List */}
            <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', overflowX: 'hidden' }}>
                {/* GROUPS */}
                <Box display="flex" alignItems="center" px={2} py={0.5} justifyContent="space-between">
                    <Typography sx={{ color: 'text.secondary', fontWeight: 600, fontSize: 13 }}>
                        Groups
                    </Typography>
                    <IconButton
                        aria-label="Create Group"
                        size="small"
                        onClick={() => onGroupManage?.()} // No param = create
                    >
                        <Icon fontSize="small">add</Icon>
                    </IconButton>
                </Box>
                {groups && groups.length > 0 && (
                    <>
                        {groups
                            .filter(g => g.name.toLowerCase().includes(search.toLowerCase()))
                            .map((group: any) => {
                                const hasUnread = hasUnreadMessages("group", group.id,
                                    clearedUnread, unreadUserIds, group.hasUnread, incomingMessages, activeRecipient, activeGroup);

                                return (
                                    <ListItemButton key={'group-' + group.id} onClick={() => {
                                        onSelectGroup(group);
                                        //Clear unread for this group
                                        setIncomingMessages((msgs: any[]) => msgs.filter(m => m.groupId !== group.id));
                                        setClearedUnread(_msg => ({ ..._msg, [keyGroup(group.id)]: true }));
                                    }}>
                                        <ListItemAvatar>
                                            <Box position="relative">
                                                <Avatar sx={{ bgcolor: '#1976d2' }}>
                                                    <Icon fontSize="small">groups</Icon>
                                                </Avatar>
                                                {hasUnread && (
                                                    <Icon sx={{
                                                        color: '#d32f2f', fontSize: 18,
                                                        position: 'absolute', top: 0, right: 0,
                                                        background: 'white', borderRadius: '50%', padding: '1px',
                                                    }}>
                                                        mail_outline
                                                    </Icon>
                                                )}
                                            </Box>
                                        </ListItemAvatar>
                                        <ListItemText
                                            primary={
                                                <Box display="flex" alignItems="center">
                                                    <span style={{ flex: 1 }}>{group.name}</span>
                                                    <IconButton
                                                        size="small"
                                                        sx={{ ml: 1 }}
                                                        aria-label="Edit Group"
                                                        onClick={e => {
                                                            e.stopPropagation();
                                                            onGroupManage?.(group);
                                                        }}
                                                    >
                                                        <Icon fontSize="small">edit</Icon>
                                                    </IconButton>
                                                </Box>
                                            }
                                            secondary={group.members && group.members.length > 0
                                                ? `${group.members.length} members` : undefined}
                                        />
                                    </ListItemButton>
                                );
                            })}
                    </>
                )}
                <List dense>
                    <Typography sx={{ px: 2, py: 0.5, color: 'text.secondary', fontWeight: 600, fontSize: 13 }}>
                        Users
                    </Typography>
                    {sortedUsers.map((user: any) => {
                  
                        const hasUnread = hasUnreadMessages("user", user.id,
                            clearedUnread, unreadUserIds, user.hasUnread, incomingMessages, activeRecipient, activeGroup);

                        return (
                            <ListItemButton key={user.id} onClick={() => {
                                onSelectUser(user);
                                //Clear unread for this user
                                setIncomingMessages((msgs: any) =>
                                    msgs.filter((m: any) => !(m.fromUserId === user.id && m.toUserId === currentUser.id))
                                );
                                setClearedUnread(_msg => ({ ..._msg,[keyUser(user.id)]: true }));
                            }}>
                                <ListItemAvatar>
                                    <Box position="relative">
                                        <Avatar sx={{ bgcolor: user.online ? '#44b700' : '' }}>{user.name[0]}</Avatar>
                                        {hasUnread && (
                                            <Icon
                                                sx={{
                                                    color: '#d32f2f',
                                                    fontSize: 18,
                                                    position: 'absolute',
                                                    top: 0,
                                                    right: 0,
                                                    background: 'white',
                                                    borderRadius: '50%',
                                                    padding: '1px',
                                                }}
                                            >
                                                mail_outline
                                            </Icon>
                                        )}
                                    </Box>
                                </ListItemAvatar>
                                <ListItemText
                                    primaryTypographyProps={{ fontWeight: user.hasUnread ? 700 : 400 }}
                                    primary={user.name}
                                    secondary={user.online ? 'Online' : 'Offline'}
                                />
                            </ListItemButton>

                        );
                    })}
                </List>
            </Box>
        </Paper>
    );
}
